import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { Input } from "@shared/components/ui/input";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@shared/components/ui/tabs";
import { Package, User, Truck, RefreshCw, Filter, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import InfiniteScrollTrigger from "@shared/components/InfiniteScrollTrigger";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import * as pickupService from "@features/pickups/services/pickupService";
import { getAllLivreurs } from "@features/packages/services/packagesService";
import { useUsersSearchQuery } from "@features/users/hooks/useUsers";
import PickupCardForLivreurManagement from "./PickupCardForLivreurManagement";
import HeadLayout from "@shared/layouts/HeadLayout";

// Pickup Status Filter Options
const PICKUP_STATUS_FILTERS = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "picked_up", label: "Picked Up" },
  { value: "in_transit", label: "In Transit" },
  { value: "signed", label: "Signed" },
  { value: "failed", label: "Failed" },
];

// Client Selector Component
const ClientSelector = ({
  selectedClient,
  clientSearch,
  setClientSearch,
  clients,
  isLoadingClients,
  onClientSelect,
  onClientClear,
  t,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      {selectedClient ? (
        <div className="flex items-center gap-2 p-2 border rounded-md bg-blue-50 dark:bg-blue-950/20">
          <User className="h-4 w-4 text-blue-600" />
          <Badge variant="secondary" className="flex items-center gap-1">
            {selectedClient.brandName || selectedClient.fullName}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClientClear}
              className="h-4 w-4 p-0 ml-1 hover:bg-red-100"
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        </div>
      ) : (
        <div className="relative">
          <Input
            placeholder={t("Search clients...")}
            value={clientSearch}
            onChange={(e) => {
              setClientSearch(e.target.value);
              setIsOpen(e.target.value.length >= 2);
            }}
            onFocus={() => setIsOpen(clientSearch.length >= 2)}
            onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          />
          {isOpen && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white dark:bg-gray-800 border rounded-md shadow-lg max-h-60 overflow-y-auto">
              {isLoadingClients ? (
                <div className="p-2 text-center">
                  <ThreeDotLoader />
                </div>
              ) : clients.length > 0 ? (
                clients.map((client) => (
                  <button
                    key={client._id}
                    className="w-full text-left p-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    onClick={() => {
                      onClientSelect(client);
                      setIsOpen(false);
                    }}
                  >
                    <User className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="font-medium">
                        {client.brandName || client.fullName}
                      </div>
                      {client.email && (
                        <div className="text-sm text-muted-foreground">
                          {client.email}
                        </div>
                      )}
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-2 text-center text-muted-foreground">
                  {t("No clients found")}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Livreur Selector Component
const LivreurSelector = ({
  selectedLivreur,
  livreurSearch,
  setLivreurSearch,
  livreurs,
  isLoadingLivreurs,
  onLivreurSelect,
  onLivreurClear,
  t,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Filter livreurs based on search
  const filteredLivreurs = livreurs?.livreurs?.filter((livreur) => {
    const searchTerm = livreurSearch.toLowerCase();
    return (
      livreur.fullName?.toLowerCase().includes(searchTerm) ||
      livreur.email?.toLowerCase().includes(searchTerm) ||
      livreur.phone?.includes(searchTerm)
    );
  });

  return (
    <div className="relative">
      {selectedLivreur ? (
        <div className="flex items-center gap-2 p-2 border rounded-md bg-green-50 dark:bg-green-950/20">
          <Truck className="h-4 w-4 text-green-600" />
          <Badge variant="secondary" className="flex items-center gap-1">
            {selectedLivreur.fullName || selectedLivreur.email}
            <Button
              variant="ghost"
              size="sm"
              onClick={onLivreurClear}
              className="h-4 w-4 p-0 ml-1 hover:bg-red-100"
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        </div>
      ) : (
        <div className="relative">
          <Input
            placeholder={t("Search livreurs...")}
            value={livreurSearch}
            onChange={(e) => {
              setLivreurSearch(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          />
          {isOpen && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white dark:bg-gray-800 border rounded-md shadow-lg max-h-60 overflow-y-auto">
              {isLoadingLivreurs ? (
                <div className="p-2 text-center">
                  <ThreeDotLoader />
                </div>
              ) : filteredLivreurs && filteredLivreurs.length > 0 ? (
                filteredLivreurs.map((livreur) => (
                  <button
                    key={livreur._id}
                    className="w-full text-left p-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    onClick={() => {
                      onLivreurSelect(livreur);
                      setIsOpen(false);
                    }}
                  >
                    <Truck className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="font-medium">
                        {livreur.fullName || livreur.email}
                      </div>
                      {livreur.phone && (
                        <div className="text-sm text-muted-foreground">
                          {livreur.phone}
                        </div>
                      )}
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-2 text-center text-muted-foreground">
                  {t("No livreurs found")}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export function ImprovedPickupLivreurManagement() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [params, setParams] = useState({ limit: 20 });
  const [activeTab, setActiveTab] = useState("all");
  const [clientSearch, setClientSearch] = useState("");
  const [livreurSearch, setLivreurSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedLivreur, setSelectedLivreur] = useState(null);

  // Client search query
  const { data: clientsData, isLoading: isLoadingClients } =
    useUsersSearchQuery(clientSearch, 10, 1, clientSearch.length >= 2, {
      role: "client",
    });

  // Fetch livreurs
  const { data: livreursData, isLoading: isLoadingLivreurs } = useQuery({
    queryKey: ["admin-livreurs"],
    queryFn: getAllLivreurs,
  });

  const livreurs = livreursData || { livreurs: [] };
  const clients = clientsData?.data || [];

  // Initialize filters from URL
  useEffect(() => {
    const status = searchParams.get("status");
    const search = searchParams.get("search");
    const user = searchParams.get("user");
    const livreur = searchParams.get("livreur");

    const urlFilters = {};
    if (status) urlFilters.status = status;
    if (search) urlFilters.search = search;
    if (user) urlFilters.user = user;
    if (livreur) urlFilters.livreur = livreur;

    if (Object.keys(urlFilters).length > 0) {
      setParams((prev) => ({ ...prev, ...urlFilters, page: 1 }));
    }
  }, [searchParams]);

  // Build query params
  const buildPickupParams = (pageParam = 1) => {
    const queryParams = new URLSearchParams({
      page: pageParam,
      limit: params.limit || 20,
    });

    if (params.status && params.status !== "all") {
      queryParams.append("status", params.status);
    }

    if (params.search) {
      queryParams.append("search", params.search);
    }

    if (params.user) {
      queryParams.append("user", params.user);
    }

    if (params.livreur && params.livreur !== "all") {
      queryParams.append("livreur", params.livreur);
    }

    return queryParams.toString();
  };

  // Fetch all pickups with infinite scroll
  const {
    data: allPickupsData,
    fetchNextPage: fetchNextAllPage,
    hasNextPage: hasNextAllPage,
    isFetchingNextPage: isFetchingNextAllPage,
    isLoading: isLoadingAll,
    error: allError,
    refetch: refetchAll,
  } = useInfiniteQuery({
    queryKey: ["admin-pickups-infinite", params],
    queryFn: async ({ pageParam = 1 }) => {
      return pickupService.fetchAllPickups(buildPickupParams(pageParam));
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo?.nextPage || undefined;
    },
    enabled: activeTab === "all",
  });

  // Fetch unassigned pickups
  const {
    data: unassignedPickupsData,
    fetchNextPage: fetchNextUnassignedPage,
    hasNextPage: hasNextUnassignedPage,
    isFetchingNextPage: isFetchingNextUnassignedPage,
    isLoading: isLoadingUnassigned,
    error: unassignedError,
    refetch: refetchUnassigned,
  } = useInfiniteQuery({
    queryKey: ["admin-pickups-unassigned", params],
    queryFn: async ({ pageParam = 1 }) => {
      const queryParams = new URLSearchParams({
        page: pageParam,
        limit: params.limit || 20,
        hasLivreur: "false",
      });

      if (params.status && params.status !== "all") {
        queryParams.append("status", params.status);
      }

      if (params.search) {
        queryParams.append("search", params.search);
      }

      if (params.user) {
        queryParams.append("user", params.user);
      }

      return pickupService.fetchAllPickups(queryParams.toString());
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo?.nextPage || undefined;
    },
    enabled: activeTab === "unassigned",
  });

  const allPickups =
    allPickupsData?.pages?.flatMap((page) => page.pickups) || [];
  const totalAllPickups = allPickupsData?.pages?.[0]?.count || 0;

  const unassignedPickups =
    unassignedPickupsData?.pages?.flatMap((page) => page.pickups) || [];
  const totalUnassignedPickups = unassignedPickupsData?.pages?.[0]?.count || 0;

  // Calculate stats
  const getStatsForPickups = (pickups) => {
    return {
      total: pickups.length,
      assigned: pickups.filter((p) => p.livreur).length,
      unassigned: pickups.filter((p) => !p.livreur).length,
      signed: pickups.filter((p) => p.status === "signed").length,
      inProgress: pickups.filter((p) =>
        ["picked_up", "in_transit"].includes(p.status)
      ).length,
      pending: pickups.filter((p) => ["pending", "approved"].includes(p.status))
        .length,
    };
  };

  const handleFilter = (key, value) => {
    const apiValue = value === "all" ? "" : value;
    const newParams = { ...params, [key]: apiValue, page: 1 };
    setParams(newParams);

    const newSearchParams = new URLSearchParams(searchParams);
    if (apiValue) {
      newSearchParams.set(key, apiValue);
    } else {
      newSearchParams.delete(key);
    }
    setSearchParams(newSearchParams);
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setClientSearch("");
    handleFilter("user", client._id);
  };

  const handleClientClear = () => {
    setSelectedClient(null);
    handleFilter("user", "");
  };

  const handleLivreurSelect = (livreur) => {
    setSelectedLivreur(livreur);
    setLivreurSearch("");
    handleFilter("livreur", livreur._id);
  };

  const handleLivreurClear = () => {
    setSelectedLivreur(null);
    handleFilter("livreur", "");
  };

  const handleRefresh = () => {
    if (activeTab === "all") {
      refetchAll();
    } else {
      refetchUnassigned();
    }
    toast.success(t("Data refreshed"));
  };

  const handleClearFilters = () => {
    setParams({ limit: 20 });
    setSearchParams(new URLSearchParams());
    setSelectedClient(null);
    setSelectedLivreur(null);
    setClientSearch("");
    setLivreurSearch("");
  };

  if (allError || unassignedError) {
    toast.error(t("Failed to load pickups"));
  }

  const renderPickupsList = (
    pickups,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  ) => (
    <div className="space-y-4">
      {isLoading ? (
        <div className="flex justify-center py-8">
          <ThreeDotLoader />
        </div>
      ) : pickups.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              {t("No pickups found")}
            </h3>
            <p className="text-muted-foreground">
              {params.status || params.search || params.user || params.livreur
                ? t("No pickups match your current filters")
                : t("No pickups available yet")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4">
            {pickups.map((pickup) => (
              <PickupCardForLivreurManagement
                key={pickup._id}
                pickupData={pickup}
                onSuccess={handleRefresh}
              />
            ))}
          </div>

          {hasNextPage && (
            <div className="flex justify-center pt-4">
              <InfiniteScrollTrigger
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <>
      <HeadLayout title="Pickup Livreur Management" />
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {t("Pickup Livreur Management")}
            </h1>
            <p className="text-muted-foreground">
              {t("Manage pickup assignments and monitor pickup status")}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isLoadingAll || isLoadingUnassigned}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${
                isLoadingAll || isLoadingUnassigned ? "animate-spin" : ""
              }`}
            />
            {t("Refresh")}
          </Button>
        </div>

        {/* Main Content with Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="space-y-6"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="all">{t("All Pickups")}</TabsTrigger>
            <TabsTrigger value="unassigned">
              {t("Unassigned")} ({totalUnassignedPickups})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              {(() => {
                const stats = getStatsForPickups(allPickups);
                return (
                  <>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold">
                          {totalAllPickups}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("Total Pickups")}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-green-600">
                          {stats.assigned}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("Assigned")}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-orange-600">
                          {stats.unassigned}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("Unassigned")}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-blue-600">
                          {stats.signed}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("Signed")}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-yellow-600">
                          {stats.inProgress}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("In Progress")}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-gray-600">
                          {stats.pending}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("Pending")}
                        </p>
                      </CardContent>
                    </Card>
                  </>
                );
              })()}
            </div>

            {/* Filters */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Filter className="h-5 w-5" />
                    {t("Filter Pickups")}
                  </CardTitle>
                  {(params.status ||
                    params.search ||
                    params.user ||
                    params.livreur) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearFilters}
                    >
                      {t("Clear Filters")}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <Select
                    value={params.status || "all"}
                    onValueChange={(value) => handleFilter("status", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("Filter by status")} />
                    </SelectTrigger>
                    <SelectContent>
                      {PICKUP_STATUS_FILTERS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {t(option.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <ClientSelector
                    selectedClient={selectedClient}
                    clientSearch={clientSearch}
                    setClientSearch={setClientSearch}
                    clients={clients}
                    isLoadingClients={isLoadingClients}
                    onClientSelect={handleClientSelect}
                    onClientClear={handleClientClear}
                    t={t}
                  />

                  <LivreurSelector
                    selectedLivreur={selectedLivreur}
                    livreurSearch={livreurSearch}
                    setLivreurSearch={setLivreurSearch}
                    livreurs={livreurs}
                    isLoadingLivreurs={isLoadingLivreurs}
                    onLivreurSelect={handleLivreurSelect}
                    onLivreurClear={handleLivreurClear}
                    t={t}
                  />

                  <Input
                    placeholder={t("Search pickups...")}
                    value={params.search || ""}
                    onChange={(e) => handleFilter("search", e.target.value)}
                    className="md:col-span-2"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Pickups List */}
            {renderPickupsList(
              allPickups,
              isLoadingAll,
              hasNextAllPage,
              isFetchingNextAllPage,
              fetchNextAllPage
            )}
          </TabsContent>

          <TabsContent value="unassigned" className="space-y-6">
            {/* Stats Cards for Unassigned */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {(() => {
                const stats = getStatsForPickups(unassignedPickups);
                return (
                  <>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-orange-600">
                          {totalUnassignedPickups}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("Unassigned Pickups")}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-gray-600">
                          {stats.pending}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("Pending")}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-blue-600">
                          {livreurs.livreurs?.length || 0}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("Available Livreurs")}
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-purple-600">
                          {stats.inProgress}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {t("In Progress")}
                        </p>
                      </CardContent>
                    </Card>
                  </>
                );
              })()}
            </div>

            {/* Filters for Unassigned */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Filter className="h-5 w-5" />
                    {t("Filter Unassigned Pickups")}
                  </CardTitle>
                  {(params.status || params.search || params.user) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearFilters}
                    >
                      {t("Clear Filters")}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Select
                    value={params.status || "all"}
                    onValueChange={(value) => handleFilter("status", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("Filter by status")} />
                    </SelectTrigger>
                    <SelectContent>
                      {PICKUP_STATUS_FILTERS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {t(option.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <ClientSelector
                    selectedClient={selectedClient}
                    clientSearch={clientSearch}
                    setClientSearch={setClientSearch}
                    clients={clients}
                    isLoadingClients={isLoadingClients}
                    onClientSelect={handleClientSelect}
                    onClientClear={handleClientClear}
                    t={t}
                  />

                  <Input
                    placeholder={t("Search pickups...")}
                    value={params.search || ""}
                    onChange={(e) => handleFilter("search", e.target.value)}
                    className="md:col-span-2"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Unassigned Pickups List */}
            {renderPickupsList(
              unassignedPickups,
              isLoadingUnassigned,
              hasNextUnassignedPage,
              isFetchingNextUnassignedPage,
              fetchNextUnassignedPage
            )}
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

export default ImprovedPickupLivreurManagement;
